import React, { ReactNode, lazy, Suspense, useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { useHistory } from 'react-router';
import { Auth } from 'aws-amplify';
import { ThemeProvider } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useAsyncResource } from 'use-async-resource';

import { initAmplify, pingCognito } from 'amplify';
import { initIamApi } from 'api/iamApi';
import SkeletonForm from 'components/SkeletonForm';
import { useStore } from 'store';
import { CustomTheme, makeTheme, overrideBaseTheme, ThemeNames } from 'theme/';
import { CustomThemeOptions } from 'theme/themeTypes';
import themes from 'theme/themes';
import { ERROR_TYPES, LOG_ERROR, SET_THEME } from 'utils/constants';
import { getQueryAsObject } from 'utils/query';
import sendMessage, { setMessagePrefix } from 'utils/sendMessage';
import routes from 'utils/routes';
import 'App.module.scss';

const ChangePassword = lazy(() => import('pages/ChangePassword'));
const CompleteSignUp = lazy(() => import('pages/CompleteSignUp'));
const CreateAccount = lazy(() => import('pages/CreateAccount'));
const EmailUpdate = lazy(() => import('pages/EmailUpdate'));
const ForgotPassword = lazy(() => import('pages/ForgotPassword'));
const Login = lazy(() => import('pages/Login'));
const NotFound = lazy(() => import('pages/NotFound'));
const PasswordExpired = lazy(() => import('pages/PasswordExpired'));
const PasswordReset = lazy(() => import('pages/PasswordReset'));

const fetchAppSettings = async (): Promise<any> => (await fetch(`${process.env.PUBLIC_URL}/settings.json`)).json();

const Container = ({ children }: { children: ReactNode }): JSX.Element => {
  const {
    appSettings: {
      actions: { setAppSettings },
    },
    alert: {
      state: { text },
      actions: { setError },
    },
  } = useStore();

  const [appSettingsReader] = useAsyncResource(fetchAppSettings, []);

  const appSettings = appSettingsReader();
  const awsSettings = initAmplify(appSettings);
  initIamApi(appSettings);

  useEffect(() => {
    if (appSettings) {
      setAppSettings(appSettings);
    }
    // appSettings and setAppSettings do not support being dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [healthyReader] = useAsyncResource(pingCognito, awsSettings.region);
  const healthy = healthyReader();

  useEffect(() => {
    if (!healthy) {
      sendMessage(LOG_ERROR, `Network error pinging cognito in region: ${awsSettings.region}`);
      setError(ERROR_TYPES.NETWORK_FAILURE(awsSettings.region, appSettings.apiDomain));
    }
    // setError does not support being a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthy]);

  useEffect(() => {
    const loadOrLogoutUser = async (): Promise<void> => {
      const { logout } = getQueryAsObject();

      if (logout) {
        await Auth.signOut({ global: true });
      }
    };
    loadOrLogoutUser();
  }, []);

  const history = useHistory();

  useEffect(() => {
    const onChange = (): void => {
      if (text) {
        setError();
      }
    };
    history.listen(onChange);
  }, [setError, text, history]);

  return <div>{children}</div>;
};

export default (): JSX.Element | null => {
  const {
    appSettings: {
      actions: { setAppSettings },
    },
    alert: { state: alertState },
  } = useStore();

  const {
    theme: themeName,
    messagePrefix,
    domain,
    portalCode,
  }: {
    theme?: ThemeNames;
    messagePrefix?: string;
    domain?: string;
    portalCode?: string | number;
  } = getQueryAsObject();
  const [incomingTheme, setIncomingTheme] = useState<null | CustomTheme>(makeTheme());

  useEffect(() => {
    if (themeName) setIncomingTheme(makeTheme(themeName));
    if (messagePrefix) setMessagePrefix(messagePrefix);
    if (domain) setAppSettings({ domain });
    if (portalCode) setAppSettings({ portalCode: portalCode.toString() });
    // setAppSettings shouldn't be part of the dependencies but react-hooks/exhaustive-deps doesn't know that
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeName, messagePrefix, domain, portalCode]);

  useLayoutEffect(() => {
    const listenMessages = ({ data }: { data: { type: string; payload: CustomThemeOptions } }): void => {
      if (typeof data === 'object' && data.type === SET_THEME) {
        setIncomingTheme(overrideBaseTheme(themeName ? themes[themeName] : {}, data.payload));
      }
    };

    window.addEventListener('message', listenMessages);
    return (): void => {
      window.removeEventListener('message', listenMessages);
    };
  }, [themeName]);

  return (
    incomingTheme && (
      <form
        style={{ padding: incomingTheme?.custom?.app?.padding }}
        onSubmit={(event): void => event.preventDefault()}
      >
        <ThemeProvider theme={incomingTheme}>
          <Suspense fallback={<SkeletonForm />}>
            <Router>
              <Container>
                {alertState.text && <Alert severity={alertState.severity}>{alertState.text}</Alert>}
                <Switch>
                  <Route exact path={routes.login} component={Login} />
                  <Route exact path={routes.changePassword} component={ChangePassword} />
                  <Route exact path={routes.completeSignUp} component={CompleteSignUp} />
                  <Route exact path={routes.createAccount} component={CreateAccount} />
                  <Route exact path={routes.emailUpdate} component={EmailUpdate} />
                  <Route exact path={routes.forgotPassword} component={ForgotPassword} />
                  <Route exact path={routes.passwordExpired} component={PasswordExpired} />
                  <Route exact path={routes.passwordReset} component={PasswordReset} />
                  <Route path='/*' component={NotFound} />
                </Switch>
              </Container>
            </Router>
          </Suspense>
        </ThemeProvider>
      </form>
    )
  );
};
